import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "./Modal";
import { useApp } from "../context/AppContext";
import { assistantApi } from "../services/api";
import { templateEmail } from "../services/assistant";
import { copyText } from "../utils/download";

const KIND_TITLES = {
  recruiter: "Email the recruiter",
  hiringManager: "Email the hiring manager",
  team: "Email a senior peer",
};

// Drafts an outreach email to a contact and records it on the job once sent.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ComposeEmailModal({ job, contact, onClose }) {
  const {
    profile,
    completeStep,
    updateJob,
    toast,
    integrations,
    documents,
    downloadResume,
  } = useApp();
  const [email, setEmail] = useState(contact.email || "");
  const [savingEmail, setSavingEmail] = useState(false);
  const trimmedEmail = email.trim().toLowerCase();
  const emailValid = EMAIL_RE.test(trimmedEmail);
  const emailDirty = trimmedEmail !== (contact.email || "");
  const resumes = useMemo(() => documents.resumes || [], [documents.resumes]);
  const templates = useMemo(
    () => documents.emailTemplates || [],
    [documents.emailTemplates],
  );
  const matching = useMemo(
    () => templates.filter((t) => !contact.kind || t.kind === contact.kind),
    [templates, contact.kind],
  );
  const isFollowUp = Boolean(contact.emailed);
  const defaultTemplate = matching.find((t) => t.isDefault) || matching[0];
  const [templateId, setTemplateId] = useState(
    isFollowUp ? "" : defaultTemplate?.id || "",
  );
  const selectedTemplate = isFollowUp
    ? null
    : matching.find((t) => t.id === templateId);
  const [draft] = useState(() =>
    templateEmail({
      job,
      contact,
      profile,
      template: isFollowUp ? "" : defaultTemplate?.content,
    }),
  );
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [writing, setWriting] = useState(integrations.ai);
  const [sending, setSending] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const gmailConnected = integrations.configured?.gmailConnected;
  const gmailEmail = integrations.configured?.gmailEmail;
  const gmailConfigured = integrations.configured?.gmail;
  // Emails only ever send from the user's own Gmail - there is no Plateo-address sending.
  const canSendDirect = gmailConnected;
  const edited = useRef(false);

  useEffect(() => {
    if (!integrations.ai) {
      const fallback = templateEmail({
        job,
        contact,
        profile,
        template: isFollowUp ? "" : selectedTemplate?.content,
      });
      if (!edited.current) {
        setSubject(fallback.subject);
        setBody(fallback.body);
      }
      return undefined;
    }
    let cancelled = false;
    setWriting(true);
    assistantApi
      .emailDraft(
        job.id,
        contact.id,
        isFollowUp ? undefined : templateId || undefined,
      )
      .then(({ draft: ai }) => {
        if (cancelled || edited.current) return;
        setSubject(ai.subject);
        setBody(ai.body);
      })
      .catch(
        (err) =>
          !cancelled &&
          toast(
            `${err.message} Using a basic ${isFollowUp ? "follow-up" : "template"} instead.`,
            "info",
          ),
      )
      .finally(() => !cancelled && setWriting(false));
    return () => {
      cancelled = true;
    };
  }, [integrations.ai, job.id, contact.id, isFollowUp, templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (setter) => (e) => {
    edited.current = true;
    setter(e.target.value);
  };

  const pickTemplate = (id) => {
    edited.current = false;
    setTemplateId(id);
  };

  // Saves the typed email onto the contact, so it's there next time. Resolves to false on failure.
  const saveEmail = async () => {
    if (!emailValid) return false;
    setSavingEmail(true);
    const saved = await updateJob(job.id, {
      contacts: job.contacts.map((c) =>
        c.id === contact.id ? { ...c, email: trimmedEmail } : c,
      ),
    });
    setSavingEmail(false);
    if (saved) toast(`Email saved for ${contact.name}`);
    return Boolean(saved);
  };

  const markSent = async () => {
    setSending(true);
    const saved = await completeStep(
      job.id,
      "emailed",
      `${contact.emailed ? "Followed up with" : "Emailed"} ${contact.name}`,
      {
        contacts: job.contacts.map((c) =>
          c.id === contact.id
            ? {
                ...c,
                emailed: true,
                email: emailValid ? trimmedEmail : c.email,
              }
            : c,
        ),
      },
    );
    setSending(false);
    if (!saved) return;
    toast(`Email to ${contact.name} recorded`);
    onClose();
  };

  // Attachment is a property of the chosen template, not of this one email.
  const attachedResume = selectedTemplate?.attachResume
    ? resumes.find((r) => r.id === selectedTemplate.resumeId)
    : null;

  const hasEmail = emailValid;
  const mailto = hasEmail
    ? `mailto:${trimmedEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : null;

  // Plateo sends it directly - the only way the resume can actually be attached.
  // (Email-app links can't carry files, so that path never downloads anything anymore.)
  const sendNow = async () => {
    if (!emailValid) return;
    setSendingNow(true);
    try {
      const result = await assistantApi.sendEmail(job.id, {
        contactId: contact.id,
        email: trimmedEmail,
        subject,
        body,
        emailTemplateId: isFollowUp ? undefined : templateId || undefined,
      });
      // Sync the "emailed" / thread state the server just saved into the local job list.
      const savedContact = result.job?.contacts?.find((c) => c.id === contact.id);
      await updateJob(job.id, {
        contacts: job.contacts.map((c) =>
          c.id === contact.id
            ? {
                ...c,
                emailed: true,
                email: trimmedEmail,
                gmailThreadId:
                  savedContact?.gmailThreadId || c.gmailThreadId || "",
                rfcMessageId:
                  savedContact?.rfcMessageId || c.rfcMessageId || "",
              }
            : c,
        ),
      });
      toast(
        result.attached
          ? `Email sent to ${contact.name} with ${result.attached} attached`
          : result.threaded
            ? `Follow-up sent to ${contact.name} in the same email thread`
            : `Email sent to ${contact.name}${result.via === "gmail" ? ` from your Gmail` : ""}`,
      );
      onClose();
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setSendingNow(false);
    }
  };

  const openInEmailApp = async () => {
    if (!mailto) return;
    // Keep a newly typed email on the contact before handing off to the mail app.
    if (emailDirty && !(await saveEmail())) return;
    window.location.href = mailto;
  };

  return (
    <Modal
      show
      onClose={onClose}
      title={
        contact.emailed
          ? "Send a follow-up"
          : KIND_TITLES[contact.kind] || "Email contact"
      }
      size="modal-lg"
      footer={
        <>
          <button
            className="btn btn-light me-auto"
            onClick={async () =>
              toast(
                (await copyText(`Subject: ${subject}\n\n${body}`))
                  ? "Email copied"
                  : "Couldn't copy",
                "info",
              )
            }
          >
            <i className="bi bi-clipboard me-1" /> Copy
          </button>
          {attachedResume && (
            <button
              type="button"
              className="btn btn-light"
              title="Only needed if you send from your own email app - Send email attaches it for you"
              onClick={async () => {
                await downloadResume(attachedResume);
                toast(`Downloaded ${attachedResume.name}`, "info");
              }}
            >
              <i className="bi bi-paperclip me-1" /> {attachedResume.name}
            </button>
          )}
          {mailto && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={openInEmailApp}
            >
              <i className="bi bi-envelope me-1" /> Open in email app
            </button>
          )}
          <button
            className="btn btn-light"
            onClick={markSent}
            disabled={sending || !hasEmail}
            title="For when you sent it yourself, e.g. from your email app"
          >
            <i className="bi bi-check-lg me-1" /> Mark as sent
          </button>
          {!canSendDirect && (
            <span className="small text-muted">
              <i className="bi bi-google me-1" />
              {gmailConfigured ? (
                <>
                  Connect Gmail in order to send mail from Plateo -{" "}
                  <Link to="/profile" onClick={onClose}>
                    connect in Profile
                  </Link>
                </>
              ) : (
                "Gmail isn't set up on this server yet"
              )}
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={sendNow}
            disabled={sendingNow || !hasEmail || !canSendDirect}
            title={
              !hasEmail
                ? "Add the person's email above first"
                : !canSendDirect
                  ? "Connect Gmail in your Profile first"
                  : ""
            }
          >
            {sendingNow ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />{" "}
                Sending…
              </>
            ) : (
              <>
                <i className="bi bi-send me-1" />{" "}
                {isFollowUp
                  ? "Send follow-up"
                  : attachedResume
                    ? "Send email with resume"
                    : "Send email"}
              </>
            )}
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label small fw-medium" htmlFor="email-to">
          To - {contact.name}
        </label>
        <div className="d-flex gap-2">
          <input
            id="email-to"
            className="form-control"
            type="email"
            placeholder="Their email, if you found it (e.g. with Hunter.io or Apollo.io)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {emailDirty && (
            <button
              type="button"
              className="btn btn-outline-primary flex-shrink-0"
              onClick={saveEmail}
              disabled={!emailValid || savingEmail}
            >
              {savingEmail ? "Saving…" : "Save email"}
            </button>
          )}
        </div>
        <div className="form-text">
          {emailDirty && !emailValid
            ? "That doesn't look like an email address yet."
            : hasEmail
              ? emailDirty
                ? "Unsaved - Save email, or just open your email app / mark as sent and we'll save it then."
                : "Saved on this contact."
              : "No email yet? Draft here and copy it into a LinkedIn message - or type their email above if you found it yourself, and we'll save it on the contact."}
        </div>
      </div>
      {isFollowUp ? (
        <p className="small text-muted">
          Follow-ups are written fresh (no outreach template) so they don’t
          sound like another first email.{" "}
          {canSendDirect
            ? "Send email replies in the same Gmail thread when we still have the earlier message."
            : null}
        </p>
      ) : matching.length > 0 ? (
        <div className="mb-3">
          <label
            className="form-label small fw-medium"
            htmlFor="email-template"
          >
            Your template
          </label>
          <select
            id="email-template"
            className="form-select"
            value={templateId}
            onChange={(e) => pickTemplate(e.target.value)}
          >
            {matching.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.isDefault ? " (default)" : ""}
              </option>
            ))}
          </select>
          <div className="form-text">
            We rewrite this template for this job and person, keeping your
            voice.
            {attachedResume
              ? ` This template attaches ${attachedResume.name} - “Send email” includes it automatically. (Your own email app can't take attachments from here; if you use it, download the resume with the paperclip button and attach it yourself.)`
              : selectedTemplate?.attachResume
                ? " This template attaches a resume that is no longer available."
                : ""}
          </div>
        </div>
      ) : (
        <p className="small text-muted">
          No outreach template for this kind of contact yet.{" "}
          <Link to="/documents#email-templates" onClick={onClose}>
            Add one in Documents
          </Link>{" "}
          and drafts will sound like you. Meanwhile we'll write a plain draft.
        </p>
      )}
      <div className="mb-3">
        <label className="form-label small fw-medium" htmlFor="email-subject">
          Subject
        </label>
        <input
          id="email-subject"
          className="form-control"
          value={subject}
          onChange={edit(setSubject)}
        />
      </div>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label
            className="form-label small fw-medium mb-0"
            htmlFor="email-body"
          >
            Message
          </label>
          {writing && (
            <span className="small text-muted">
              <span className="spinner-border spinner-border-sm me-1" />{" "}
              Personalizing…
            </span>
          )}
        </div>
        <textarea
          id="email-body"
          className="form-control"
          rows={10}
          value={body}
          onChange={edit(setBody)}
        />
        <div className="form-text">
          {integrations.ai
            ? isFollowUp
              ? "A short follow-up from the job and your resume - not your first-email template. Edit anything before sending."
              : selectedTemplate
                ? "Written from your template, the job description and your resume. Edit anything before sending."
                : "Written from the job description and your resume. Edit anything before sending."
            : isFollowUp
              ? "A basic follow-up draft. Personalized AI drafts are available by request."
              : "A basic template. Personalized AI drafts are available by request."}{" "}
          {gmailConnected ? (
            isFollowUp
              ? `“Send email” sends from your Gmail (${gmailEmail}) in the same thread as your earlier message when possible.`
              : `“Send email” sends from your Gmail (${gmailEmail}) - replies land in your inbox like any email you send.`
          ) : gmailConfigured ? (
            <>
              Emails only send from your own address.{" "}
              <Link to="/profile" onClick={onClose}>
                Connect Gmail in your Profile
              </Link>{" "}
              first, then come back and send - or open your email app below and
              send it yourself.
            </>
          ) : (
            "Gmail sending isn't set up on this server yet, so send from your email app and mark it as sent."
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ComposeEmailModal;
